import React, { useCallback, useEffect, useState, useRef } from "react";
import SettingsIcon from "../../../../Assets/SettingsPages/SettingsIcon.svg";
import { ReactComponent as ExclamationWarningIcon } from "../../../../Assets/ActionIcons/ExclamationWarningIcon.svg";
import { useSettingsStore } from "../../../../Store/Settings";
import { useAuth0 } from "@auth0/auth0-react";
import { SettingsPatch } from "../../../../Contracts/Settings";
import ConfigurePaymentMethodCard from "./ConfigurePaymentMethodCard";
import { SettingsPagesChildTabProps } from "../index";
import { useOnboardingStore } from "../../../../Store/Onboarding";
import { PaymentRoutes } from "../../../../Contracts/Routes";
import { useTeamStore } from "../../../../Store/Team";
import { PaymentsPartnerCountry } from "../../../../Contracts/Common";

const ConfigurePaymentMethodsTab: React.FC<SettingsPagesChildTabProps> = ({
  currentTabWorkDone
}) => {
  const { isLoading, settings, getSettings, updateSettings } = useSettingsStore();
  const { adyenKYCDetails } = useOnboardingStore();
  const { currentTeam, getCurrentTeam } = useTeamStore();
  const { getAccessTokenSilently } = useAuth0();
  const [dirtySettings, setDirtySettings] = useState<SettingsPatch>({});
  const [chargeStatementDescriptorError, setChargeStatementDescriptorError] = useState(false);
  const descriptorErrorRef = useRef<HTMLDivElement>(null);
  const refreshThings = useCallback(async () => {
    await Promise.all([
      getSettings(getAccessTokenSilently),
      getCurrentTeam(getAccessTokenSilently)
    ]);
  }, [getAccessTokenSilently, getSettings, getCurrentTeam]);
  useEffect(() => {
    refreshThings();
  }, [refreshThings]);
  const saveAndContinue = async () => {
    if (validateChargeStatementDescriptor()) return;
    const emptyFeeTitle = updatedSettings.adyen_partner_configured_payment_methods?.some(
      method =>
        method.is_surcharges_fee_enable &&
        (method.surcharges_fee_description ?? "").trim().length === 0
    );
    const emptyFee = updatedSettings.adyen_partner_configured_payment_methods?.some(
      method =>
        method.is_surcharges_fee_enable &&
        (method.surcharges_fee_percentage || 0) === 0 &&
        (method.surcharges_fee_fixed || 0) === 0
    );
    if (emptyFeeTitle || emptyFee) {
      return;
    }
    if (Object.keys(dirtySettings).length !== 0) {
      const success = await updateSettings(getAccessTokenSilently, dirtySettings);
      if (!success) {
        setDirtySettings({});
        return;
      }
    }
    setDirtySettings({});
    currentTabWorkDone();
  };
  const updatedSettings = { ...settings, ...dirtySettings };
  const isDirty = dirtySettings ? Object.keys(dirtySettings).length > 0 : false;
  const validateChargeStatementDescriptor = (): boolean => {
    if ((updatedSettings.payment_charge_statement_descriptor ?? "").trim().length === 0) {
      setChargeStatementDescriptorError(true);
      return true;
    } else {
      setChargeStatementDescriptorError(false);
      return false;
    }
  };

  useEffect(() => {
    if (chargeStatementDescriptorError) {
      descriptorErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [chargeStatementDescriptorError]);

  return (
    <>
      <div
        className={`relative flex flex-col h-[calc(100vh-16rem)] overflow-y-auto ${
          isLoading ? "cursor-progress" : ""
        }`}
      >
        <div className="md:w-2/4 h-full">
          <div className="flex mt-8">
            <img src={SettingsIcon} alt="Reminder Frequency" />
            <div className="font-semibold text-gray-9 text-m ml-2">Payment Configuration</div>
          </div>
          <div className="ml-8">
            <div className="text-gray-800 mb-8 mt-2">
              The payment portal will have the below configuration that will be displayed
            </div>
            <div className="font-semibold mb-2">Payment Method</div>
            <div className="mb-4 text-gray-800">
              Select the payment methods that will be available for your clients
            </div>
            {adyenKYCDetails?.is_kyc_completed ? (
              <>
                {updatedSettings.adyen_partner_configured_payment_methods?.map(method => (
                  <ConfigurePaymentMethodCard
                    method={method}
                    onChange={method =>
                      setDirtySettings({
                        ...dirtySettings,
                        adyen_partner_configured_payment_methods:
                          updatedSettings.adyen_partner_configured_payment_methods?.map(m =>
                            m.type === method.type ? method : m
                          )
                      })
                    }
                  />
                ))}
                <small className="text-gray-500">
                  {currentTeam?.country === PaymentsPartnerCountry.USA
                    ? "Direct Debit fees are 0.8% + $0.30 capped at $5.00."
                    : "Direct Debit fees are 1% + ￡0,20 capped at ￡4,00."}
                  <br />
                  {currentTeam?.country === PaymentsPartnerCountry.USA
                    ? "Processing fees for card payments are 2.9% + $0.30 per transaction."
                    : "Processing fees for VISA or Mastercard card payments are 1.9% + ￡0,20 per transaction. American Express fee is 2.9% + ￡0,20 per transaction."}
                </small>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <ExclamationWarningIcon className="text-red-600 w-6 h-6" />
                <div className="text-gray-500">
                  Please start the{" "}
                  <a
                    href={PaymentRoutes.ONBOARDING_FORM}
                    className="cursor-pointer text-blue-600 hover:underline"
                  >
                    customer identification
                  </a>{" "}
                  process first. Payment methods can be configured as soon as your application is
                  reviewed.
                </div>
              </div>
            )}

            <div className="my-8">
              <div className="font-semibold">Charge statement descriptor</div>
              <div className="text-gray-500 my-2">
                Configure the text that will show up in the client's bank statements. This applies
                to card payments only. Direct Debit transactions will appear as "Zomentum" or "Adyen
                NV".
              </div>
              <input
                className="px-3 py-1 border border-gray-300 rounded my-2"
                value={updatedSettings.payment_charge_statement_descriptor}
                onChange={e =>
                  setDirtySettings({
                    ...dirtySettings,
                    payment_charge_statement_descriptor: e.target.value
                  })
                }
              />
              {chargeStatementDescriptorError && (
                <div className="text-red-600" ref={descriptorErrorRef}>
                  Charge statement descriptor is required
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <footer className="items-end w-full border-gray-200 border-t pt-4 pl-2">
        <button
          className="bg-blue-500  text-white px-4 py-2 rounded hover:bg-blue-700"
          onClick={saveAndContinue}
        >
          {isDirty ? "Save And Continue" : "Next"}
        </button>
        {isDirty && (
          <button
            className="hover:text-red-600 text-gray-500 px-4 py-2 ml-2"
            onClick={() => setDirtySettings({})}
          >
            Discard
          </button>
        )}
      </footer>
    </>
  );
};

export default ConfigurePaymentMethodsTab;
